import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Landmark, Coins, TrendingUp, Clock3, ReceiptText, ShieldCheck, ArrowRightLeft, FileText } from "lucide-react";
import { toast } from "sonner";

const peso = (value) => `₱${Number(value || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RevenueCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [urgentReason, setUrgentReason] = useState("");
  const [urgentBookingId, setUrgentBookingId] = useState("");
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    api.get("/revenue-center")
      .then((res) => setData(res.data))
      .catch((err) => toast.error(err.response?.data?.detail || "Unable to load revenue center"))
      .finally(() => setLoading(false));
  }, []);

  const availableViews = useMemo(() => {
    const views = [];
    if (data?.provider) views.push("venue");
    if (data?.coach_sp) views.push("coach");
    return views;
  }, [data]);

  const activeView = useMemo(() => {
    const queryView = searchParams.get("view");
    if (queryView && availableViews.includes(queryView)) return queryView;
    if (availableViews.includes("venue") && user?.active_persona === "VENUE_OWNER") return "venue";
    if (availableViews.includes("coach") && user?.active_persona === "COACH_SP") return "coach";
    return availableViews[0] || "venue";
  }, [availableViews, searchParams, user]);

  const setView = (view) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", view);
    setSearchParams(params);
  };

  const submitUrgentRequest = async () => {
    if (!urgentBookingId.trim()) {
      toast.error("Enter a booking reference for urgent payout");
      return;
    }
    setRequesting(true);
    try {
      await api.post("/revenue-center/provider/urgent-request", { booking_id: urgentBookingId.trim(), reason: urgentReason.trim() });
      toast.success("Urgent payout request submitted");
      setUrgentBookingId("");
      setUrgentReason("");
      const refreshed = await api.get("/revenue-center");
      setData(refreshed.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setRequesting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return <div className="max-w-4xl mx-auto px-4 py-20 text-center font-heading text-xl">Revenue center unavailable</div>;
  if (availableViews.length === 0) return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-8" data-testid="revenue-center-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Coins className="w-8 h-8 text-court-orange" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">Revenue Center</h1>
          </div>
          <p className="text-muted-foreground max-w-3xl">Unified settlements, commissions, payout visibility, split history, and revenue tracking for venue owners and coach / sports professional personas.</p>
        </div>
        <Badge className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">Active Persona: {user?.active_persona_label || user?.active_persona}</Badge>
      </div>

      {availableViews.length > 1 ? (
        <Tabs value={activeView} onValueChange={setView}>
          <TabsList className="grid grid-cols-2 w-full max-w-xl">
            {availableViews.includes("venue") && <TabsTrigger value="venue" data-testid="revenue-tab-venue">Venue Revenue</TabsTrigger>}
            {availableViews.includes("coach") && <TabsTrigger value="coach" data-testid="revenue-tab-coach">Coach / SP Revenue</TabsTrigger>}
          </TabsList>
          <TabsContent value="venue" className="mt-6">{data.provider && <VenueRevenueView provider={data.provider} submitUrgentRequest={submitUrgentRequest} urgentBookingId={urgentBookingId} setUrgentBookingId={setUrgentBookingId} urgentReason={urgentReason} setUrgentReason={setUrgentReason} requesting={requesting} />}</TabsContent>
          <TabsContent value="coach" className="mt-6">{data.coach_sp && <CoachRevenueView coach={data.coach_sp} navigate={navigate} />}</TabsContent>
        </Tabs>
      ) : availableViews[0] === "venue" ? (
        <VenueRevenueView provider={data.provider} submitUrgentRequest={submitUrgentRequest} urgentBookingId={urgentBookingId} setUrgentBookingId={setUrgentBookingId} urgentReason={urgentReason} setUrgentReason={setUrgentReason} requesting={requesting} />
      ) : (
        <CoachRevenueView coach={data.coach_sp} navigate={navigate} />
      )}
    </div>
  );
}

function VenueRevenueView({ provider, submitUrgentRequest, urgentBookingId, setUrgentBookingId, urgentReason, setUrgentReason, requesting }) {
  const summary = provider?.summary || {};
  const exportStatementCsv = () => {
    const header = "booking_id,venue_name,gross_amount,commission_amount,other_adjustment,net_payout,payout_status,payout_reference\n";
    const rows = (provider.settlements || []).map((item) => `${item.booking_id},${item.venue_name},${item.gross_amount},${item.commission_amount},${item.other_adjustment},${item.net_payout},${item.payout_status},${item.payout_reference}`);
    const blob = new Blob([header + rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "venue-statement-of-account.csv";
    a.click();
  };

  const openStatementPdf = () => {
    window.print();
  };
  return (
    <div className="space-y-6" data-testid="venue-revenue-view">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {[
          { label: "Gross Booking Amount", value: peso(summary.gross_booking_amount), icon: TrendingUp, color: "text-court-orange" },
          { label: "SetPlay Commission", value: peso(summary.setplay_commission), icon: ReceiptText, color: "text-red-500" },
          { label: "Estimated Net Payout", value: peso(summary.estimated_net_payout), icon: Coins, color: "text-green-600" },
          { label: "Pending Payout", value: peso(summary.pending_payout), icon: Clock3, color: "text-blue-600" },
          { label: "On Hold", value: peso(summary.on_hold_amount), icon: ShieldCheck, color: "text-amber-600" },
        ].map((card) => (
          <Card key={card.label} data-testid={`venue-revenue-card-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <CardContent className="p-5">
              <card.icon className={`w-5 h-5 ${card.color} mb-2`} />
              <p className="font-heading text-2xl font-bold">{card.value}</p>
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1">{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><ArrowRightLeft className="w-4 h-4" />Commercial Terms</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {(provider.venue_terms || []).map((term) => (
              <div key={term.venue_id} className="rounded-xl border border-slate-200 p-3" data-testid={`venue-term-${term.venue_id}`}>
                <p className="font-medium text-sm">{term.venue_name}</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-muted-foreground">
                  <p>Revenue model: <span className="text-foreground font-medium">{term.revenue_model}</span></p>
                  <p>Commission: <span className="text-foreground font-medium">{term.commission_rate}%</span></p>
                  <p>Venue share: <span className="text-foreground font-medium">{term.venue_share_pct}%</span></p>
                  <p>Payout frequency: <span className="text-foreground font-medium capitalize">{term.payout_frequency}</span></p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Landmark className="w-4 h-4" />Verified Payout Accounts</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {(provider.bank_accounts || []).map((account) => (
              <div key={account.venue_id} className="rounded-xl border border-slate-200 p-3" data-testid={`payout-account-${account.venue_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-sm">{account.venue_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{account.payout_method.replace(/_/g, ' ')}</p>
                  </div>
                  <Badge className={account.is_verified ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>{account.is_verified ? "Verified" : "Pending"}</Badge>
                </div>
                <div className="mt-2 text-xs text-muted-foreground space-y-1">
                  {account.bank_name && <p>Bank: <span className="text-foreground font-medium">{account.bank_name}</span></p>}
                  {account.account_name && <p>Account Name: <span className="text-foreground font-medium">{account.account_name}</span></p>}
                  {account.account_number && <p>Account #: <span className="text-foreground font-medium">{account.account_number}</span></p>}
                  {account.gcash_number && <p>GCash: <span className="text-foreground font-medium">{account.gcash_number}</span></p>}
                  {account.maya_number && <p>Maya: <span className="text-foreground font-medium">{account.maya_number}</span></p>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Urgent Payout Requests</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Same-bank standard payout fee: {peso(provider.fee_rules?.standard_same_bank_fee || 0)} · Interbank fee: {peso(provider.fee_rules?.standard_interbank_fee || 0)} · Urgent fee: {peso(provider.fee_rules?.urgent_payout_fee || 0)}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em]">Booking Reference</Label>
              <Input value={urgentBookingId} onChange={(e) => setUrgentBookingId(e.target.value)} placeholder="book_..." data-testid="urgent-payout-booking-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em]">Reason</Label>
              <Input value={urgentReason} onChange={(e) => setUrgentReason(e.target.value)} placeholder="Why do you need this payout urgently?" data-testid="urgent-payout-reason-input" />
            </div>
            <Button onClick={submitUrgentRequest} disabled={requesting || !urgentBookingId} className="w-full rounded-full bg-court-orange hover:bg-court-orange-hover text-white" data-testid="submit-urgent-payout-request-btn">
              {requesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Request Urgent Payout
            </Button>
            {(provider.urgent_requests || []).length > 0 && (
              <div className="space-y-2 pt-2">
                {(provider.urgent_requests || []).slice(0, 5).map((request) => (
                  <div key={request.request_id} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`urgent-request-${request.request_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{request.booking_id}</span>
                      <Badge variant="outline" className="uppercase text-[10px]">{request.status}</Badge>
                    </div>
                    {request.reason && <p className="text-xs text-muted-foreground mt-1">{request.reason}</p>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><FileText className="w-4 h-4" />Statements & Pricing Rules</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-xl border border-slate-200 p-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Commission Rules</span><span>{provider.pricing_policies?.commission_rules?.length || 0}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Subscription Fees</span><span>{provider.pricing_policies?.subscription_fees?.length || 0}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Special Rates</span><span>{provider.pricing_policies?.special_rates?.length || 0}</span></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={exportStatementCsv} data-testid="venue-export-statement-csv">CSV Statement</Button>
              <Button size="sm" variant="outline" onClick={openStatementPdf} data-testid="venue-export-statement-pdf">PDF Statement</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Settlement Breakdown</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm" data-testid="venue-settlements-table">
              <thead>
                <tr className="border-b text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <th className="py-3 pr-3">Booking</th>
                  <th className="py-3 pr-3">Venue</th>
                  <th className="py-3 pr-3">Gross</th>
                  <th className="py-3 pr-3">Commission</th>
                  <th className="py-3 pr-3">Fees</th>
                  <th className="py-3 pr-3">Net Payout</th>
                  <th className="py-3 pr-3">Status</th>
                  <th className="py-3">Reference</th>
                </tr>
              </thead>
              <tbody>
                {(provider.settlements || []).map((settlement) => (
                  <tr key={settlement.settlement_id} className="border-b border-slate-100" data-testid={`settlement-row-${settlement.booking_id}`}>
                    <td className="py-3 pr-3">
                      <p className="font-medium">{settlement.booking_id}</p>
                      <p className="text-xs text-muted-foreground">{settlement.booking_date}</p>
                    </td>
                    <td className="py-3 pr-3">{settlement.venue_name}</td>
                    <td className="py-3 pr-3">{peso(settlement.gross_amount)}</td>
                    <td className="py-3 pr-3">
                      <p>{peso(settlement.commission_amount)}</p>
                      <p className="text-xs text-muted-foreground">{settlement.commission_rate}%</p>
                    </td>
                    <td className="py-3 pr-3 text-xs text-muted-foreground">
                      <p>Interbank: {peso(settlement.interbank_fee)}</p>
                      <p>Urgent: {peso(settlement.urgent_fee)}</p>
                      <p>Refund: {peso(settlement.refund_deduction)}</p>
                    </td>
                    <td className="py-3 pr-3 font-medium text-green-700">{peso(settlement.net_payout)}</td>
                    <td className="py-3 pr-3">
                      <Badge className={settlement.payout_status === "on_hold" ? "bg-amber-100 text-amber-800" : settlement.payout_status === "ready_for_payout" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-700"}>{settlement.payout_status.replace(/_/g, " ")}</Badge>
                      {settlement.hold_reason && <p className="text-[10px] text-muted-foreground mt-1 max-w-[180px]">{settlement.hold_reason}</p>}
                    </td>
                    <td className="py-3">{settlement.payout_reference}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Payout Batches</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(provider.payout_batches || []).map((batch) => {
            const batchId = batch.batch_id || batch.payout_batch_id;
            const batchStatus = batch.status || batch.batch_status || "draft";
            return (
            <div key={batchId} className="rounded-xl border border-slate-200 p-3 flex items-center justify-between gap-4" data-testid={`payout-batch-${batchId}`}>
              <div>
                <p className="font-medium">{batch.payout_date}</p>
                <p className="text-xs text-muted-foreground">{batch.settlements_count} settlements · {batchStatus.replace(/_/g, ' ')}</p>
              </div>
              <div className="text-right">
                <p className="font-medium">{peso(batch.net_payout)}</p>
                <p className="text-xs text-muted-foreground">Gross {peso(batch.gross_amount)}</p>
              </div>
            </div>
          );})}
        </CardContent>
      </Card>
    </div>
  );
}

function CoachRevenueView({ coach, navigate }) {
  const summary = coach?.summary || {};
  const exportStatementCsv = () => {
    const header = "source,reference_id,date,gross_amount,platform_commission,net_amount,status\n";
    const rows = (coach.history || []).map((item) => `${item.source},${item.reference_id},${item.date},${item.gross_amount},${item.platform_commission},${item.net_amount},${item.status}`);
    const blob = new Blob([header + rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "coach-sp-statement-of-account.csv";
    a.click();
  };

  const openStatementPdf = () => {
    window.print();
  };
  return (
    <div className="space-y-6" data-testid="coach-revenue-view">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {[
          { label: "Gross Revenue", value: peso(summary.gross_revenue), icon: TrendingUp, color: "text-court-orange" },
          { label: "Platform Commission", value: peso(summary.platform_commission), icon: ReceiptText, color: "text-red-500" },
          { label: "Released Earnings", value: peso(summary.released_earnings), icon: Coins, color: "text-green-600" },
          { label: "Pending Escrow", value: peso(summary.pending_escrow), icon: Clock3, color: "text-amber-600" },
          { label: "Referral Earnings", value: peso(summary.referral_earnings), icon: ShieldCheck, color: "text-purple-600" },
        ].map((card) => (
          <Card key={card.label} data-testid={`coach-revenue-card-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <CardContent className="p-5">
              <card.icon className={`w-5 h-5 ${card.color} mb-2`} />
              <p className="font-heading text-2xl font-bold">{card.value}</p>
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1">{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Split Rules</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Default SetPlay Commission</span><span>{coach.split_rules?.default_commission_pct || 10}%</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Referral Commission</span><span>{coach.split_rules?.referral_commission_pct || 5}%</span></div>
            <p className="text-xs text-muted-foreground pt-2">Session and SP booking splits stay auditable because net and commission values are stored per completed payout or booking record.</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Statements & Pricing Rules</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Commission Rules</span><span>{coach.pricing_policies?.commission_rules?.length || 0}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Subscription Fees</span><span>{coach.pricing_policies?.subscription_fees?.length || 0}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Special Rates</span><span>{coach.pricing_policies?.special_rates?.length || 0}</span></div>
            <div className="flex gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={exportStatementCsv} data-testid="coach-export-statement-csv">CSV Statement</Button>
              <Button size="sm" variant="outline" onClick={openStatementPdf} data-testid="coach-export-statement-pdf">PDF Statement</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="xl:col-span-2">
          <CardHeader><CardTitle className="font-heading text-base">Monthly Revenue Trend</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(coach.monthly_trend || []).length === 0 ? <p className="text-sm text-muted-foreground">No monthly revenue yet.</p> : (coach.monthly_trend || []).map((item) => (
              <div key={item.month} className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`monthly-revenue-${item.month}`}>
                <span className="font-medium">{item.month}</span>
                <span>{peso(item.earnings)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Revenue History</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm" data-testid="coach-history-table">
              <thead>
                <tr className="border-b text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  <th className="py-3 pr-3">Source</th>
                  <th className="py-3 pr-3">Reference</th>
                  <th className="py-3 pr-3">Date</th>
                  <th className="py-3 pr-3">Gross</th>
                  <th className="py-3 pr-3">Commission</th>
                  <th className="py-3 pr-3">Net</th>
                  <th className="py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {(coach.history || []).map((item, idx) => (
                  <tr key={`${item.reference_id}-${idx}`} className="border-b border-slate-100" data-testid={`coach-history-row-${idx}`}>
                    <td className="py-3 pr-3">
                      <p className="font-medium">{item.source}</p>
                      <p className="text-xs text-muted-foreground">{item.title}</p>
                    </td>
                    <td className="py-3 pr-3">{item.reference_id}</td>
                    <td className="py-3 pr-3">{item.date}</td>
                    <td className="py-3 pr-3">{peso(item.gross_amount)}</td>
                    <td className="py-3 pr-3 text-red-600">-{peso(item.platform_commission)}</td>
                    <td className="py-3 pr-3 text-green-700 font-medium">{peso(item.net_amount)}</td>
                    <td className="py-3"><Badge variant="outline" className="uppercase text-[10px]">{String(item.status).replace(/_/g, ' ')}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {userHasReceiptCapableRows(coach.history) && (
            <div className="pt-4 text-right">
              <Button variant="outline" onClick={() => navigate('/sp/earnings')} className="rounded-full">Open Legacy SP History</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function userHasReceiptCapableRows(history = []) {
  return history.some((item) => item.source === "Sports Professional Booking");
}